import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingModal = ({ open, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    number_of_projects: '',
    team_size: '',
    crm_used: '',
    primary_use_case: '',
    sales_setup: '',
    pain_points: '',
    how_did_you_hear: '',
  });

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/onboarding/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast.success('Welcome aboard!');
      onComplete();
    } catch {
      toast.error('Something went wrong. You can complete this later.');
      onComplete();
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await fetch(`${API_URL}/api/v1/onboarding/skip`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* ignore */ }
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[480px] [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                {step === 1 ? 'Tell us about your business' : 'Almost there'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Step {step} of 2 — helps us set up your experience
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">How many projects do you manage?</Label>
                  <Select value={form.number_of_projects} onValueChange={v => update('number_of_projects', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="onboarding-projects">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 project</SelectItem>
                      <SelectItem value="2-5">2-5 projects</SelectItem>
                      <SelectItem value="6-10">6-10 projects</SelectItem>
                      <SelectItem value="11-20">11-20 projects</SelectItem>
                      <SelectItem value="20+">20+ projects</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Team size</Label>
                  <Select value={form.team_size} onValueChange={v => update('team_size', v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="onboarding-team">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">1-5 people</SelectItem>
                      <SelectItem value="6-15">6-15 people</SelectItem>
                      <SelectItem value="16-50">16-50 people</SelectItem>
                      <SelectItem value="50+">50+ people</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">What CRM do you currently use?</Label>
                <Select value={form.crm_used} onValueChange={v => update('crm_used', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="onboarding-crm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="None">None / Excel / Google Sheets</SelectItem>
                    <SelectItem value="Salesforce">Salesforce</SelectItem>
                    <SelectItem value="HubSpot">HubSpot</SelectItem>
                    <SelectItem value="Sell.Do">Sell.Do</SelectItem>
                    <SelectItem value="LeadSquared">LeadSquared</SelectItem>
                    <SelectItem value="Custom">Custom / In-house</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Primary use case</Label>
                <Select value={form.primary_use_case} onValueChange={v => update('primary_use_case', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="onboarding-usecase">
                    <SelectValue placeholder="What will you primarily use this for?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inventory_tracking">Real-time inventory tracking</SelectItem>
                    <SelectItem value="channel_management">Channel partner management</SelectItem>
                    <SelectItem value="sales_control">Sales process control</SelectItem>
                    <SelectItem value="reporting">Reporting and analytics</SelectItem>
                    <SelectItem value="all">All of the above</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">How is your sales team currently set up?</Label>
                <Select value={form.sales_setup} onValueChange={v => update('sales_setup', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="onboarding-sales-setup">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_house_only">In-house sales team only</SelectItem>
                    <SelectItem value="cp_only">Channel partners only</SelectItem>
                    <SelectItem value="mixed">Both in-house + channel partners</SelectItem>
                    <SelectItem value="setting_up">Still setting up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">What are your biggest pain points today?</Label>
                <Textarea
                  value={form.pain_points}
                  onChange={e => update('pain_points', e.target.value)}
                  placeholder="e.g., Double bookings, no real-time visibility, CP conflicts..."
                  rows={3}
                  className="text-sm"
                  data-testid="onboarding-pain-points"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">How did you hear about Nine Degree?</Label>
                <Select value={form.how_did_you_hear} onValueChange={v => update('how_did_you_hear', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="onboarding-source">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google Search</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="industry_event">Industry Event</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={handleSkip} data-testid="onboarding-skip">
            Skip for now
          </Button>
          <div className="flex gap-2">
            {step === 2 && (
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>Back</Button>
            )}
            {step === 1 ? (
              <Button size="sm" onClick={() => setStep(2)} data-testid="onboarding-next">
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={handleSubmit} disabled={loading} data-testid="onboarding-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Get Started
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
